'use client';

import { Users, PlusIcon, Pencil, Trash2, AlertTriangle, Mail } from 'lucide-react';

import { useCompletion, experimental_useObject as useObject } from 'ai/react';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AIEmailCampaign, AIEmailCampaignStatus, AIStrategy } from '@prisma/client';
import EmailStrategy, { EmailStrategyAreaHandle } from './strategy-content';
import { RelevantPromptDialog } from '@/components/ui/prompt-dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import * as Tabs from '@radix-ui/react-tabs';
import { OutreachForm, OutreachStep } from '@/components/ui/outreach-form';
import { z } from 'zod';
import { Session } from 'next-auth';

export default function DashboardClient({
	domainParam,
	goalParam,
	session,
	siteId,
	useRealLeads
}: {
	domainParam: string | null;
	goalParam: string | null;
	session: Session;
	siteId: string;
	useRealLeads: boolean | null;
}) {
	const refSiteId = useRef<string>(siteId);
	const loadMorePromptRef = useRef<HTMLButtonElement>(null);
	const findEmailsRef = useRef<HTMLButtonElement>(null);
	const formAreaRef = useRef<HTMLDivElement>(null);
	const emailStrategyRef = useRef<EmailStrategyAreaHandle>(null);

	const [prompts, setPrompts] = useState<RelevantPrompts[]>([]);
	const [loadCount, setLoadCount] = useState(0);
	const [loadMoreLoading, setLoadMoreLoading] = useState(false);
	const [lowestRelevance, setLowestRelevance] = useState(10000);
	const [strategy, setStrategy] = useState<AIStrategy | null>(null);
	const [activeTab, setActiveTab] = useState('personas');
	const [isAutoRun, setIsAutoRun] = useState(false);
	const [flagIsStreamingIdeas, setFlagIsStreamingIdeas] = useState(false);
	const [generatingMoreEmails, setGeneratingMoreEmails] = useState(false);

	// Handle Deep Link
	useEffect(() => {
		if (!session) {
			console.error('No session found');
			return;
		}
		const executeSteps = async () => {
			let _strategy = null;
			if (session.user) {
				_strategy = await fetchStrategy(refSiteId.current);
				console.log('Strategy:', _strategy);
			}
			// Not in deep link flow so ignore
			if (!domainParam) {
				return;
			}
			// If found strategy then ignore deep link flow
			if (_strategy) {
				return;
			}
			setIsAutoRun(true);
			// This is for fetching demo site | assumed the middleware switches the user if they are guest and have a query param for domain
			if (session.siteId) {
				const strategy = await fetchStrategy(session.siteId);
				if (strategy) {
					console.log('Strategy already exists:', strategy);
					return;
				}
			}
			console.log(`Generating strategy for ${domainParam} session ${session}`);
			if (!domainParam.includes('http')) {
				domainParam = `https://${domainParam}`;
				setUrl(domainParam);
			} else {
				setUrl(domainParam);
			}

			setOutreachFormLoading(true);
			setProductInfoPlaceholder(`Getting product info for ${domainParam}`);
			setProductInfoFormType('description');
			const _productInfo = await getStreamProductInfo('', {
				body: {
					productInfo: '',
					url: domainParam
				}
			});

			if (!_productInfo) {
				alert('Failed to get product info');
				setOutreachFormLoading(false);
				setProductInfoPlaceholder('Enter a description of your product or service');
				return;
			}

			console.log(`Submitting product info with ${_productInfo} and ${domainParam}`);

			getStreamedIdeas({
				prompts: prompts,
				query: _productInfo ?? domainParam
			});
			// Set a little gap - so people can see
			setTimeout(() => {
				setStep(OutreachStep.Goal);
			}, 1000);
		};
		executeSteps();
	}, []);
	// End of Handle Deep Link

	const { complete: getStreamProductInfo, completion: crawledProductInfo } = useCompletion({
		api: `/api/crawl-sites/stream`,
		streamProtocol: 'text',
		onResponse(response) {
			console.log(`Crawl response: ${response}`);
			setOutreachFormLoading(false);
		},
		onFinish(prompt, completion) {
			console.log(`Crawl completion: ${completion}`);
			setOutreachFormLoading(false);
		}
	});

	useEffect(() => {
		if (crawledProductInfo !== productInfo) {
			setProductInfo(crawledProductInfo);
		}
	}, [crawledProductInfo]);

	// End of section on streaming crawl sites

	// Start on section on stream ideas
	const {
		object: streamedIdeas,
		submit: getStreamedIdeas,
		isLoading: isStreamingIdeas
	} = useObject({
		api: '/api/search-ideas/stream',
		// Should be like List<RelevantPrompts>
		schema: z.object({
			results: z.array(
				z.object({
					prompt: z.string(),
					reason: z.string(),
					relevance: z.number(),
					examples: z.array(z.string()),
					rolePrompt: z.string(),
					company_count: z.number()
				})
			)
		}),
		onError(error) {
			console.error('Streaming Ideas Error:', error);
			setOutreachFormLoading(false);
			setProductInfoPlaceholder('Enter a description of your product or service');
		},
		onFinish(event) {
			console.log('Streaming Ideas Finished:', event);
			setOutreachFormLoading(false);
			setProductInfoPlaceholder('Enter a description of your product or service');
		}
	});

	const handleStreamingIdeasStopping = () => {
		console.log(`Stopping streaming ideas`);
		if (isAutoRun) {
			if (formAreaRef.current) {
				formAreaRef.current.scrollIntoView({
					behavior: 'smooth',
					block: 'start'
				});
				const goalWhyPrompt = goalParam ?? 'Reply to email';
				setGoal(goalWhyPrompt);
				handleStepChange(OutreachStep.Goal, {
					goal: goalParam || 'Reply to email',
					trial: true
				});
				// Unflag so that later stage don't run auto
				setIsAutoRun(false);
			}
		}
	};

	const handleStreamingIdeasStarting = () => {
		console.log(`Starting streaming ideas`);
	};

	// Trigger for when streaming ideas is completed
	useEffect(() => {
		// If these don't equal, then it's either starting or stopping.
		if (isStreamingIdeas != flagIsStreamingIdeas) {
			// If this was true - then it's stopping
			if (flagIsStreamingIdeas) {
				setFlagIsStreamingIdeas(false);
				handleStreamingIdeasStopping();
			} else {
				setFlagIsStreamingIdeas(true);
				handleStreamingIdeasStarting();
			}
		}
	}, [isStreamingIdeas]);

	useEffect(() => {
		if (streamedIdeas && streamedIdeas.results && streamedIdeas.results.length) {
			// Convert streamed ideas to RelevantPrompts
			const relevantPrompts: RelevantPrompts[] = [];
			for (const idea of streamedIdeas.results) {
				if (!idea) {
					continue;
				}
				// Check if prompts already in prompts
				try {
					const relevantPrompt: RelevantPrompts = {
						prompt: idea.prompt ?? '',
						rolePrompt: idea.rolePrompt ?? '',
						examples: (idea.examples ?? []).filter(
							(example): example is string => example !== undefined
						),
						company_count: idea.company_count ?? 0,
						reason: idea.reason ?? '',
						relevance: idea.relevance ?? 0
					};
					relevantPrompts.push(relevantPrompt);
				} catch (error) {
					console.error('Error converting streamed ideas:', error);
				}
			}
			setPrompts(relevantPrompts);
			if (findEmailsRef.current) {
				findEmailsRef.current.scrollIntoView({
					behavior: 'smooth', // Instant scroll (remove for smooth)
					block: 'end'
				});
			}
		}
	}, [streamedIdeas]);

	// End of section on stream ideas

	// useEffect(() => {
	// 	const checkBotServerSide = async (visitorId: string) => {
	// 		try {
	// 			const response = await fetch('/api/check-bot', {
	// 				method: 'POST',
	// 				headers: {
	// 					'Content-Type': 'application/json'
	// 				},
	// 				body: JSON.stringify({
	// 					visitorId: visitorId,
	// 					domain: domainParam
	// 				})
	// 			});

	// 			if (!response.ok) throw new Error('Bot check failed');
	// 		} catch (err) {
	// 			console.error('Bot check error:', err);
	// 		}
	// 	};

	// 	const hasCheckedBotCookie = document.cookie
	// 		.split('; ')
	// 		.find((row) => row.startsWith('hasCheckedBot='));

	// 	if (!isLoading && !error && data?.visitorId && !hasCheckedBot && !hasCheckedBotCookie) {
	// 		setHasCheckedBot(true);
	// 		document.cookie = `hasCheckedBot=true; path=/; max-age=${60 * 60 * 24 * 365}`;
	// 		checkBotServerSide(data.visitorId);
	// 	}
	// }, [data, isLoading, error, hasCheckedBot]);

	const handleRemove = (index: number) => {
		setPrompts(prompts.filter((_, i) => i !== index));
	};

	const handleStrategyRemove = async (index: number, campaignId: string) => {
		setPrompts(prompts.filter((_, i) => i !== index));
		try {
			const response = await fetch(
				`/api/sites/${refSiteId.current}/ai-email-campaigns/${campaignId}`,
				{
					method: 'PUT',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						status: AIEmailCampaignStatus.REMOVED
					})
				}
			);
			if (!response.ok) {
				alert('Failed to remove campaign');
			}
		} catch (err: any) {
			// Handle error
		}
	};

	const handleStrategyUpdate = async (index: number, prompt: RelevantPrompts) => {
		console.log(prompt);
		if (!prompt.id) {
			// Update prompt
			console.log(prompt);
			return;
		}
		const response = await fetch(
			`/api/sites/${refSiteId.current}/ai-email-campaigns/${prompt.id}`,
			{
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					prompt: prompt.prompt,
					rolePrompt: prompt.rolePrompt,
					reason: prompt.reason
				})
			}
		);
		if (!response.ok) {
			alert('Failed to update campaign');
		}
	};

	const handleGenerateMoreEmails = async (inputStrategy?: AIStrategy | undefined) => {
		if (!strategy && !inputStrategy) {
			console.warn(`handleGenerateMoreEmails: No strategy found`);
			return;
		}
		if (generatingMoreEmails) {
			console.warn('handleGenerateMoreEmails: Already generating emails');
			return;
		}
		setGeneratingMoreEmails(true);
		// If customer hasn't logged in yet - then it's a trial
		const isTrial = useRealLeads ? false : true;
		// Note due to Tab and EmailStrategy not initialized, we need to wait for it to be initialized
		if (
			!emailStrategyRef.current ||
			!emailStrategyRef.current?.handleFetchingStrategyExecutionStreams
		) {
			var waitForEmailStrategyCount = 0;
			var waitForEmailStrategy = setInterval(() => {
				console.log(
					`Waiting for email strategy to initialize ${waitForEmailStrategyCount}`
				);
				if (
					emailStrategyRef.current &&
					emailStrategyRef.current.handleFetchingStrategyExecutionStreams
				) {
					clearInterval(waitForEmailStrategy);
					emailStrategyRef.current
						.handleFetchingStrategyExecutionStreams()
						.then(async function () {
							const response = await fetch(
								`/api/sites/${refSiteId.current}/ai-strategies/send?trial=${isTrial ? true : false}`,
								{
									method: 'POST'
								}
							);
							if (!response.ok) {
								// setGeneratingMoreEmails(false);
								alert('Failed to generate more emails');
								return;
							}
						});
				} else {
					waitForEmailStrategyCount++;
					if (waitForEmailStrategyCount > 10) {
						clearInterval(waitForEmailStrategy);
						// Should refresh
					}
				}
			}, 500);
		} else {
			await emailStrategyRef.current.handleFetchingStrategyExecutionStreams();
			const response = await fetch(
				`/api/sites/${refSiteId.current}/ai-strategies/send?trial=${isTrial ? true : false}`,
				{
					method: 'POST'
				}
			);
			if (!response.ok) {
				// setGeneratingMoreEmails(false);
				alert('Failed to generate more emails');
				return;
			}
		}
	};

	const handleAddIdea = async (index: number, prompt: RelevantPrompts) => {
		if (!prompt || prompt.id) {
			alert('Invalid prompt');
			return;
		}
		const filteredPrompts = prompts.filter((_, i) => i !== index);
		setPrompts(filteredPrompts);
		try {
			const response = await fetch(`/api/sites/${refSiteId.current}/ai-strategies/ideas`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(prompt)
			});
			if (!response.ok) {
				alert('Failed to add campaign');
				return;
			}
			const data = await response.json();
			if (!data || !data?.length) {
				alert('Failed to add campaign');
				return;
			}
			console.log(data);
			// Add to first
			setPrompts([data[0], ...filteredPrompts]);
		} catch (err: any) {
			// Handle error
		}
	};

	const handleAddAllIdeas = async () => {
		try {
			const newIdeas = prompts.filter((p) => !p.id);
			if (newIdeas.length === 0) {
				return;
			}

			const response = await fetch(`/api/sites/${refSiteId.current}/ai-strategies/ideas`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					campaigns: newIdeas
				})
			});
			if (!response.ok) {
				alert('Failed to add strategy');
			}
			await fetchStrategyIdeas(refSiteId.current ?? '');
		} catch (err: any) {
			// Handle
		}
	};

	const fetchStrategy = async (siteId: string) => {
		try {
			const response = await fetch(`/api/sites/${refSiteId.current}/ai-strategies`);
			if (!response.ok) {
				throw new Error('Failed to fetch site');
			}
			const data = await response.json();
			if (data) {
				setStrategy(data);
				// If strategy is running, then set generating more emails
				if (data.isRunning) {
					setGeneratingMoreEmails(true);
				}
				setProductInfoFormType('description');
				setProductInfo(data.prompt);
				setGoal(data.whyPrompt);
				setStep(OutreachStep.Summary);
				setActiveTab('outreach');
				await fetchStrategyIdeas(siteId);
			}
			return data;
		} catch (err: any) {
			// Handle error
		}
	};

	const fetchStrategyIdeas = async (siteId: string) => {
		try {
			const response = await fetch(`/api/sites/${refSiteId.current}/ai-strategies/ideas`);
			if (!response.ok) {
				throw new Error('Failed to fetch site');
			}
			const ideas: AIEmailCampaign[] = await response.json();
			const relevantPrompts: RelevantPrompts[] = [];
			if (ideas.length > 0) {
				for (const idea of ideas) {
					relevantPrompts.push({
						prompt: idea.prompt ?? '',
						rolePrompt: idea.rolePrompt ?? '',
						examples: idea.examples,
						company_count: idea.company_count ?? 0,
						reason: idea.reason ?? '',
						relevance: idea.relevance ?? 0,
						id: idea.id,
						searched: idea.domains.length
					});
				}
				setPrompts(relevantPrompts);
			}
		} catch (err: any) {
			// Handle error
		}
	};

	const fetchIdeas = async (prompt: string, loadMore = false) => {
		// Set loading state only when loading more
		if (loadMore) setLoadMoreLoading(true);

		console.log(`Fetching ideas for ${prompt}`);

		const promptStatements = loadMore ? prompts.map((p) => p.prompt) : [];
		const queryString = loadMore
			? `query=${prompt}&prompts=${JSON.stringify(promptStatements)}`
			: `query=${prompt}`;

		try {
			const response = await fetch(`/api/sites/${refSiteId.current}/ideas?${queryString}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json'
				}
			});

			// Stop loading state if loading more
			if (loadMore) setLoadMoreLoading(false);

			if (!response.ok) {
				return;
			}

			setLoadCount(loadCount + 1);
			const res = await response.json();
			if (!res.results || res.results.length === 0) {
				return;
			}

			for (const result of res.results) {
				if (result.relevance < lowestRelevance) {
					setLowestRelevance(result.relevance);
				}
			}

			if (loadMore) {
				setPrompts([...prompts, ...res.results]);
			} else {
				setPrompts([res.results, ...prompts]);
			}

			setProductInfo(res.query);
			return {
				prompts: res.results,
				query: res.query
			};
		} catch (err: any) {
			// Stop loading state if loading more, in case of an error
			if (loadMore) setLoadMoreLoading(false);
		}
	};

	const onEditStrategy = async (strategy: AIStrategy) => {
		const editedStrategy = await fetch(`/api/sites/${refSiteId.current}/ai-strategies`, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(strategy)
		});
		const data = await editedStrategy.json();
		setStrategy(data);
	};

	const createStrategy = async (
		whyPrompt: string,
		prompt?: string,
		overridenPrompts?: any[],
		rerouteToStrategy?: boolean,
		trial?: boolean
	) => {
		try {
			if (!whyPrompt && !goal) {
				setGoal('Reply to email');
				whyPrompt = 'Reply to email';
			}
			const response = await fetch(
				`/api/sites/${refSiteId.current}/ai-strategies?trial=${trial ? true : false}`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						strategy: {
							prompt: prompt ? prompt : productInfo,
							whyPrompt: whyPrompt ? whyPrompt : goal
						},
						callbackObject: {
							url: `/api/sites/${refSiteId.current}/ai-strategies/status`,
							method: 'GET'
						},
						shouldNotTriggerRun: true,
						campaigns:
							overridenPrompts && overridenPrompts.length > 0
								? overridenPrompts
								: prompts
					})
				}
			);
			if (!response.ok) {
				return;
			}
			fetchStrategyIdeas(refSiteId.current ?? '');
			// if (rerouteToStrategy) {
			// 	router.push('/signup');
			// }
			return await response.json();
		} catch (err: any) {}
	};

	const handleStepChange = async (step: OutreachStep, options?: StepChangeOptions) => {
		if (step === OutreachStep.ProductInfo) {
			console.log(`Submitting product info with ${productInfo} and ${url}`);
			if (productInfo !== strategy?.prompt) {
				console.log(`Updating product info`);
				setOutreachFormLoading(true);
				// Check if there's no product info or url
				if (!productInfo && !url) {
					alert('Please enter a product info or URL');
					setOutreachFormLoading(false);
					return;
				}

				// If on website check if url is valid
				if (productInfoFormType === 'website') {
					if (!isValidUrl(url)) {
						alert('Please enter a valid URL');
						setOutreachFormLoading(false);
						return;
					}
				}

				if (!strategy) {
					if (productInfoFormType === 'website') {
						getStreamProductInfo('', {
							body: {
								productInfo: productInfo,
								url: url
							}
						});
						setProductInfoPlaceholder(`Getting product info for ${url}`);
						setProductInfoFormType('description');
						return;
					} else {
						getStreamedIdeas({
							prompts: prompts,
							query: productInfo
						});
						setStep(OutreachStep.Goal);
						return;
						// await fetchIdeas(`${productInfo ?? ''}${url ?? ''}`, false);
					}
				} else {
					await Promise.all([
						onEditStrategy({
							...strategy,
							prompt: productInfo
						}),
						fetchIdeas(
							`${productInfo ?? ''}${url ?? ''}`,
							prompts.length > 0 ? true : false
						)
					]);
				}
				setOutreachFormLoading(false);
			} else {
				console.log(`Skipping product info fetching since no change`);
			}
			setStep(OutreachStep.Goal);
		} else if (step === OutreachStep.Goal) {
			const goalToUse = options?.goal || goal;
			console.log(`Submitting goal ${goalToUse}`);
			if (goalToUse !== strategy?.whyPrompt) {
				setOutreachFormLoading(true);
				if (!strategy) {
					const strategy = await createStrategy(
						goalToUse,
						undefined,
						undefined,
						true,
						options?.trial ?? false
					);
					setStrategy(strategy);
					setActiveTab('outreach');
					// If this is the first time, then execute the strategy
					handleGenerateMoreEmails(strategy);
				} else {
					await Promise.all([
						handleAddAllIdeas(),
						onEditStrategy({
							...strategy,
							whyPrompt: goalToUse
						})
					]);
				}
				setOutreachFormLoading(false);
			} else {
				console.log(`Skipping goal fetching since no change`);
			}
			setStep(OutreachStep.Summary);
		} else if (step === OutreachStep.Summary) {
			console.log(`Submitting summary`);
		} else {
			console.log(`Submitting unknown`);
		}
	};

	const onEmailStatusChange = (status: string) => {
		console.log(`Email status changed to ${status}`);
		if (status === 'Strategy execution completed') {
			setEmailCreationStatus(status);
			setTimeout(() => {
				setEmailCreationStatus('');
				setGeneratingMoreEmails(false);
			}, 1000);
		} else {
			setEmailCreationStatus(status);
		}
	};

	const onProcessingChange = (isProcessing: boolean) => {
		console.log(`Processing email creation ${isProcessing}`);
	};

	const [url, setUrl] = useState('');
	const [productInfo, setProductInfo] = useState('');
	const [productInfoFormType, setProductInfoFormType] = useState<'website' | 'description'>(
		'website'
	);
	const [productInfoPlaceholder, setProductInfoPlaceholder] = useState<string>(
		'Enter a description of your product or service'
	);
	const [goal, setGoal] = useState('');
	const [step, setStep] = useState(OutreachStep.ProductInfo);
	const [outreachFormLoading, setOutreachFormLoading] = useState(false);
	const [emailCreationStatus, setEmailCreationStatus] = useState<string>('');

	const isValidUrl = (urlString: string) => {
		const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
		return urlPattern.test(urlString);
	};

	const handleAddCustomPersona = async (newPersona: RelevantPrompts) => {
		// Add to prompts array
		setPrompts([newPersona, ...prompts]);

		// If strategy exists, save to backend
		if (strategy) {
			try {
				const response = await fetch(`/api/sites/${refSiteId.current}/ai-strategies/ideas`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify(newPersona)
				});
				
				if (response.ok) {
					const data = await response.json();
					if (data && data.length > 0) {
						// Replace the temporary persona with the one from backend (which has an ID)
						setPrompts([data[0], ...prompts]);
					}
				} else {
					console.error('Failed to save custom persona to backend');
				}
			} catch (err) {
				console.error('Error saving custom persona:', err);
			}
		}
	};

	return (
		<div className="bg-white">
			<div
				className="w-full lg:max-w-none max-w-4xl mx-auto overflow-hidden"
				id="get-started"
			>
				<section
					className="w-full py-10 bg-gradient-to-br from-emerald-50 to-teal-50"
					ref={formAreaRef}
				>
					<OutreachForm
						url={url}
						setUrl={setUrl}
						productInfo={productInfo}
						setProductInfo={setProductInfo}
						goal={goal}
						setGoal={setGoal}
						step={step}
						setStep={setStep}
						productInfoFormType={productInfoFormType}
						setProductInfoFormType={setProductInfoFormType}
						outreachFormLoading={outreachFormLoading}
						handleStepChangeAction={handleStepChange}
						emailCreationStatus={emailCreationStatus}
						siteId={refSiteId?.current}
						handleGenerateMoreEmails={handleGenerateMoreEmails}
						isGeneratingMoreEmails={generatingMoreEmails}
						productInfoPlaceholder={productInfoPlaceholder}
					/>
				</section>
				{step !== OutreachStep.ProductInfo && (
					<Tabs.Root
						value={activeTab}
						onValueChange={setActiveTab}
						className="w-full max-w-7xl mx-auto p-4 bg-white"
					>
						<Tabs.List className="bg-gray-100 p-1 rounded-full">
							<div className="bg-white rounded-full p-1 flex relative">
								<motion.div
									className="absolute inset-y-1 left-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"
									initial={false}
									animate={{
										x: activeTab === 'personas' ? '0%' : '100%',
										width: 'calc(50% - 4px)'
									}}
									transition={{ type: 'spring', stiffness: 300, damping: 30 }}
								/>
								<Tabs.Trigger
									value="personas"
									className={`relative z-10 w-1/2 py-3 text-sm font-medium rounded-full transition-colors flex items-center justify-center gap-2 ${
										activeTab === 'personas'
											? 'text-white'
											: 'text-gray-700 hover:text-gray-900'
									}`}
								>
									<Users className="w-4 h-4" />
									<span className="w-20 text-center">Personas</span>
									{prompts.length > 0 && (
										<span
											className={`${
												activeTab === 'personas'
													? 'bg-white text-emerald-600'
													: 'bg-emerald-500 text-white'
											} text-xs rounded-full w-5 h-5 flex items-center justify-center`}
										>
											{prompts.length}
										</span>
									)}
								</Tabs.Trigger>
								<Tabs.Trigger
									value="outreach"
									disabled={!strategy}
									className={`relative z-10 w-1/2 py-3 text-sm font-medium rounded-full transition-colors flex items-center justify-center gap-2 ${
										activeTab === 'outreach'
											? 'text-white'
											: strategy
												? 'text-gray-700 hover:text-gray-900'
												: 'text-gray-400'
									}`}
								>
									<Mail className="w-4 h-4" />
									<span className="w-20 text-center">Outreach</span>
								</Tabs.Trigger>
							</div>
						</Tabs.List>

						<Tabs.Content value="personas" className="bg-white">
							<section className="w-full py-6 bg-white">
								<div className="container grid items-center justify-center gap-6 px-0 md:px-6 lg:gap-10">
									<div className="flex justify-end mb-4">
										<RelevantPromptDialog
											prompt={{
												prompt: '',
												rolePrompt: '',
												examples: [],
												company_count: 100,
												reason: '',
												relevance: 0.8
											}}
											onEdit={handleAddCustomPersona}
											triggerButton={
												<Button className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-semibold">
													<PlusIcon className="h-5 w-5" />
													Add New Persona
												</Button>
											}
										/>
									</div>
									<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
										{prompts.map((prompt, index) => (
											<motion.div
												key={prompt.prompt}
												initial={{ opacity: 0, y: 20 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: index * 0.1 }}
											>
												<Card key={index} data-prompt={prompt.prompt}>
													<div className="px-6 py-4 h-full rounded-lg bg-background shadow-lg cursor-pointer transform transition-all duration-300 hover:shadow-xl hover:scale-30 active:scale-20">
														<div className="flex justify-end space-x-2">
															<Button
																data-campaign={prompt.id}
																variant="ghost"
																size="icon"
																className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50"
																onClick={(e) => {
																	e.stopPropagation(); // Prevent card click event
																	// Check if data-campaign is present and not null
																	if (
																		!e.currentTarget.dataset
																			.campaign
																	) {
																		handleRemove(index);
																	} else {
																		handleStrategyRemove(
																			index,
																			e.currentTarget.dataset
																				.campaign
																		);
																	}
																}}
															>
																<Trash2 className="h-5 w-5" />
															</Button>
														</div>
														<h3 className="text-xl font-semibold text-black line-clamp-3 h-auto min-h-[4rem]">
															Relevance{' '}
															{Math.round(prompt.relevance * 100)}
															%:{' '}
															<strong className="prompt-text">
																{prompt.rolePrompt} at{' '}
																{prompt.prompt}
															</strong>
														</h3>
														<div className="flex flex-wrap gap-2 my-4">
															{prompt.examples.map(
																(example, index) => (
																	<Badge
																		key={index}
																		variant="secondary"
																		className="bg-[#E6FAF7] text-primary hover:bg-[#E6FAF7]"
																	>
																		{example}
																	</Badge>
																)
															)}
															<span className="inline-block text-muted-foreground text-sm font-semibold py-1 px-3 rounded-full">
																{prompt.company_count}+ companies
															</span>
														</div>
														<p className="text-muted-foreground text-sm">
															{prompt.reason}
														</p>
														{prompt.searched &&
														prompt.searched > prompt.company_count ? (
															<Alert
																variant={'default'}
																className="bg-amber-50 border-amber-200 mt-4"
															>
																<AlertTriangle className="h-4 w-4 text-amber-600" />
																<AlertTitle className="text-amber-800">
																	Oversearched
																</AlertTitle>
																<AlertDescription className="text-amber-700">
																	The search results (
																	{prompt.searched}) exceed the
																	estimated company count (
																	{prompt.company_count}).
																</AlertDescription>
															</Alert>
														) : null}
														<div className="flex justify-end space-x-2 mt-4">
															{strategy && !prompt.id && (
																<button
																	className="flex items-center gap-2 bg-[#01354a] text-white px-3 py-1 rounded-full hover:bg-slate-500"
																	onClick={(e) => {
																		e.stopPropagation(); // Prevent card click event
																		// Check if data-campaign is present and not null
																		handleAddIdea(
																			index,
																			prompt
																		);
																	}}
																>
																	Add <PlusIcon />
																</button>
															)}
															<RelevantPromptDialog
																prompt={prompt}
																onEdit={(prompt) => {
																	setPrompts(
																		prompts.map((p, i) =>
																			i === index ? prompt : p
																		)
																	);
																	if (prompt.id) {
																		handleStrategyUpdate(
																			index,
																			prompt
																		);
																	}
																}}
																triggerButton={
																	<button className="flex items-center gap-2 text-[#01354a] px-3 py-1 rounded-full border border-[#01354a] hover:bg-slate-100">
																		Edit Persona
																	</button>
																}
															/>
														</div>
													</div>
												</Card>
											</motion.div>
										))}
									</div>
									{prompts.length > 0 ? (
										<div className="w-full">
											<Button
												onClick={() =>
													fetchIdeas(
														`${productInfo ?? ''}${url ?? ''}`,
														true
													)
												}
												ref={loadMorePromptRef}
												variant="outline"
												className="mt-4 w-full"
												disabled={loadMoreLoading || isStreamingIdeas}
											>
												{loadMoreLoading || isStreamingIdeas ? (
													<div className="flex items-center justify-center">
														Loading{' '}
														<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
													</div>
												) : (
													`Load More`
												)}
											</Button>
											<Button
												ref={findEmailsRef}
												disabled={loadMoreLoading || isStreamingIdeas}
												onClick={async () => {
													if (formAreaRef.current) {
														formAreaRef.current.scrollIntoView({
															behavior: 'smooth',
															block: 'start'
														});
														setGoal('Reply to email');
														handleStepChange(OutreachStep.Goal, {
															goal: goal || 'Reply to email',
															trial: true
														});
													}
												}}
												className="w-full mt-5 text-white text-lg font-bold py-4 bg-gradient-to-r from-[#38B2AC] to-[#48BB78] hover:from-[#319795] hover:to-[#38A169] shadow-lg transform transition-transform hover:scale-105"
											>
												Find and Generate Emails
											</Button>
										</div>									) : (
										<div className="min-w-96">
											<Button
												onClick={() =>
													fetchIdeas(
														`${productInfo ?? ''}${url ?? ''}`,
														true
													)
												}
												ref={loadMorePromptRef}
												variant="outline"
												className="mt-4 w-full"
												disabled={loadMoreLoading || isStreamingIdeas}
											>
												{loadMoreLoading || isStreamingIdeas ? (
													<div className="flex items-center justify-center">
														Loading{' '}
														<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
													</div>
												) : (
													`Generate Persona Ideas`
												)}
											</Button>
										</div>
									)}
								</div>
							</section>
						</Tabs.Content>

						<Tabs.Content value="outreach" className="bg-white">
							{strategy && (
								<EmailStrategy
									ref={emailStrategyRef}
									domainParam={domainParam}
									onEmailStatusChange={onEmailStatusChange}
									session={session}
								/>
							)}
						</Tabs.Content>
					</Tabs.Root>
				)}
			</div>
		</div>
	);
}
